import { css } from "@linaria/core"
import { Warning } from "@mui/icons-material"
import { Box, Button, Link, Paper, Skeleton, Stack, Tooltip, Typography } from "@mui/material"

import { useQueryClient } from "@tanstack/react-query"
import { Check } from "@untitled-ui/icons-react"
import { enqueueSnackbar } from "notistack"
import { useCallback, useEffect, useState } from "react"

import Spinner from "./Spinner"
import TokenLogo from "./TokenLogo"
import { TokenPill } from "./TokenPill"
import { getSimpleAgentInfo, HopperAgentInfo } from "../ao/agent"
import {
  AgentInfo,
  cancelHopperOrder,
  cancelLimitOrder,
  OrderAgent,
  OrderType,
  readAgentInfo,
  topUpDcaOrder,
  topUpLimitOrder,
} from "../ao/agents-api"
import { useAo } from "../ao/ao"
import { useTokenInfo } from "../hooks/use-token-info"
import { PRICE_DECIMALS } from "../settings"
import { NumberFont } from "../theme"
import {
  parseBigIntAsNumber,
  formatRelative,
  formatNumberAuto,
  formatFullDate,
} from "../utils/format"
import { StopLossVariant } from "./swap/types/StopLossOrderForm"

const TITLE_MAP: Record<OrderType, string> = {
  "dca-order": "DCA Order",
  "limit-order": "Limit Order",
  "stop-order": "Stop Loss Order",
  "hopper-order": "Multi-Hop Swap",
}

type OrderRowProps = {
  order: OrderAgent
  refreshActiveOrders: () => Promise<unknown>
  refreshActivity: () => Promise<unknown>
}

export function OrderRow(props: OrderRowProps) {
  const { order, refreshActiveOrders, refreshActivity } = props

  const [fromToken] = useTokenInfo(order.token_from)
  const [toToken] = useTokenInfo(order.token_to)

  const ao = useAo()

  const [cancelLoading, setCancelLoading] = useState(false)
  const handleCancel = async () => {
    setCancelLoading(true)
    try {
      if (order.type === "hopper-order") {
        await cancelHopperOrder(order.id, ao)
      } else {
        await cancelLimitOrder(order.id, ao)
      }

      await fetchAgentInfo()
      await refreshActiveOrders()
      await refreshActivity()
    } catch (e) {
      console.error(e)
      setCancelLoading(false)
      enqueueSnackbar("Failed to cancel order", { variant: "error" })
    }
  }

  const queryClient = useQueryClient()
  const [agentInfo, setAgentInfo] = useState<AgentInfo | HopperAgentInfo | undefined>(undefined)

  const fetchAgentInfo = useCallback(async () => {
    console.log("Fetching Agent Info", order.id)
    const res =
      order.type === "hopper-order"
        ? await getSimpleAgentInfo(order.id, ao)
        : await readAgentInfo(order.id, ao)
    console.log("Fetched Agent Info", order.id, res)
    setAgentInfo(res)

    if (
      (order.type === "hopper-order" && !(res as HopperAgentInfo).CurrentPath) ||
      (res as AgentInfo).Status === "Complete" ||
      (res as AgentInfo).Status === "Canceled" ||
      (res as AgentInfo).Status === "Expired"
    ) {
      await refreshActiveOrders()
      await refreshActivity()
      queryClient.invalidateQueries({ queryKey: ["Get-Simple-Agent-Info"] })
    }
  }, [order.id, order.type, ao, refreshActiveOrders, refreshActivity, queryClient])

  useEffect(() => {
    fetchAgentInfo()

    const interval = setInterval(fetchAgentInfo, order.type === "hopper-order" ? 2_000 : 10_000)
    return () => clearInterval(interval)
  }, [order, ao, fetchAgentInfo])

  const [topUpLoading, setTopUpLoading] = useState(false)
  const handleTopUp = async () => {
    if (!agentInfo) return
    setTopUpLoading(true)
    try {
      if (agentInfo["Agent-Type"] === "dca-order") {
        await topUpDcaOrder(
          {
            input: BigInt(order.input as string),
            fromTokenId: order.token_from,
          },
          order.id,
          ao,
        )
      } else {
        await topUpLimitOrder(
          {
            input: BigInt(order.input as string),
            fromTokenId: order.token_from,
          },
          order.id,
          ao,
        )
      }
      await fetchAgentInfo()
    } catch (e) {
      console.error(e)
      enqueueSnackbar("Failed to top-up order", { variant: "error" })
    }
    setTopUpLoading(false)
  }

  let filledPrice,
    filledPercentage,
    output,
    stopTriggerPrice,
    stopLimitPrice,
    stopVariant: StopLossVariant | undefined,
    trailingDiff,
    triggerPriceHit,
    currentHop,
    totalHops

  if (order.type === "limit-order") {
    filledPrice =
      (agentInfo as AgentInfo)?.Strategy?.Primitives?.["main-limit-order"]?.["Filled-Price"] || null
    filledPercentage =
      (agentInfo as AgentInfo)?.Strategy?.Primitives?.["main-limit-order"]?.["Filled-Percentage"] ||
      "0"
    output = (agentInfo as AgentInfo)?.Strategy?.["Min-Output"] || "0"
  } else if (order.type === "stop-order") {
    filledPrice = "n/A"
    filledPercentage = "n/A"
    stopVariant =
      (agentInfo as AgentInfo)?.Strategy?.["Is-Stop-Limit"] === "true"
        ? "limit"
        : (agentInfo as AgentInfo)?.Strategy?.["Is-Trailing"] === "true"
          ? "trailing"
          : "market"
    stopTriggerPrice = (agentInfo as AgentInfo)?.Strategy?.["Stop-Price"] || "0"

    output = (agentInfo as AgentInfo)?.Strategy?.["Stop-Limit-Min-Output"] || "n/A"
    //|| (BigInt(order.input) * BigInt(stopTriggerPrice)) / 10n ** 18n

    if (stopVariant === "trailing") {
      trailingDiff = (agentInfo as AgentInfo)?.Strategy?.["Trailing-Difference"]
      stopTriggerPrice =
        (agentInfo as AgentInfo)?.Strategy?.Primitives?.["stop-trigger"]?.["Stop-Price"] || "0"
    }
    if (stopVariant === "limit") {
      stopLimitPrice = (BigInt(output) / BigInt(order.input as string)) * 10n ** 18n
      triggerPriceHit =
        (agentInfo as AgentInfo)?.Strategy?.Primitives?.["stop-trigger"]?.["Was-Hit"] === "true"
      filledPercentage =
        (agentInfo as AgentInfo)?.Strategy?.Primitives?.["stop-limit-order"]?.[
          "Filled-Percentage"
        ] || "n/A"
      filledPrice =
        (agentInfo as AgentInfo)?.Strategy?.Primitives?.["stop-limit-order"]?.["Filled-Price"] ||
        "n/A"
    }
  } else if (order.type === "hopper-order") {
    currentHop = ((agentInfo as HopperAgentInfo)?.["CurrentPathProgress"] as any)?.["CurrentHop"]
    totalHops = ((agentInfo as HopperAgentInfo)?.["CurrentPathProgress"] as any)?.["TotalHops"]
    output = "0"
  } else {
    filledPrice = (agentInfo as AgentInfo)?.Strategy?.["Filled-Price"]
    filledPercentage = (agentInfo as AgentInfo)?.Strategy?.["Filled-Percentage"] || "0"
    output = (agentInfo as AgentInfo)?.Strategy?.["Accumulated-Output"] || "0"
  }

  if (filledPrice === "n/A") filledPrice = null
  if (filledPercentage === "n/A") filledPercentage = "0"

  if (order.type === "hopper-order" && !(agentInfo as HopperAgentInfo)?.CurrentPath) return null

  return (
    <Paper
      elevation={0}
      sx={{
        overflow: "hidden",
        borderRadius: 0.5,
      }}
      variant="alt"
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          alignItems: "flex-start",
          gap: 1,
          padding: 1,
        }}
      >
        <Stack sx={{ flexShrink: 0 }}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="subtitle1" fontWeight={500}>
              {TITLE_MAP[order.type]}
            </Typography>
            {((agentInfo as AgentInfo)?.Status === "Ready" ||
              (agentInfo as AgentInfo)?.Status === "Swapping" ||
              (order.type === "hopper-order" &&
                (agentInfo as HopperAgentInfo).IsStuck !== "true")) && (
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{
                  borderRadius: 0.5,
                  height: 28,
                  width: 28,
                  background: "var(--mui-palette-buttonBgSecondary)",
                  "& svg": {
                    width: "0.85rem",
                    height: "0.85rem",
                  },
                }}
              >
                <Spinner style={{ color: "var(--mui-palette-accent-main)" }} />
              </Stack>
            )}
            {order.type === "hopper-order" && (agentInfo as HopperAgentInfo).IsStuck === "true" && (
              <Tooltip title="Something went wrong, please cancel this Multi-hop order. It looks like the Agent is stuck (trying to swap for more than 10 minutes).">
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    borderRadius: 0.5,
                    height: 28,
                    width: 28,
                    background: "rgba(var(--mui-palette-warning-mainChannel) / 0.05)",
                    "& svg": {
                      width: "0.85rem",
                      height: "0.85rem",
                    },
                  }}
                >
                  <Warning style={{ color: "var(--mui-palette-warning-main)" }} />
                </Stack>
              </Tooltip>
            )}
          </Stack>
          {order.type === "hopper-order" && currentHop !== undefined && (
            <Typography
              variant="caption"
              component={Stack}
              direction="row"
              gap={0.5}
              sx={{ maxWidth: "100%" }}
              color="secondary"
            >
              Hops{" "}
              <Typography
                color="accent.main"
                fontSize="inherit"
                fontFamily={NumberFont}
                fontWeight={500}
                component={"div"}
                sx={{
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                }}
              >
                {currentHop}/{totalHops}
              </Typography>{" "}
            </Typography>
          )}
          {order.type !== "hopper-order" &&
            (order.type !== "stop-order" ||
              (order.type === "stop-order" && stopVariant === "limit")) && (
              <>
                {filledPercentage === undefined ? (
                  <Skeleton width={110} />
                ) : (
                  <Typography
                    variant="caption"
                    component={Stack}
                    direction="row"
                    gap={0.5}
                    sx={{ maxWidth: "100%" }}
                    color="secondary"
                  >
                    Filled{" "}
                    <Typography
                      color="accent.main"
                      fontSize="inherit"
                      fontFamily={NumberFont}
                      fontWeight={500}
                      component={"div"}
                      sx={{
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                      }}
                    >
                      {parseInt(filledPercentage)}/100
                    </Typography>{" "}
                  </Typography>
                )}
              </>
            )}
          {order.type === "stop-order" &&
            (stopVariant === undefined ? (
              <Skeleton width={110} />
            ) : (
              <Typography
                variant="caption"
                component={Stack}
                direction="row"
                gap={0.5}
                sx={{
                  maxWidth: "100%",
                  textTransform: "capitalize",
                }}
                color="secondary"
              >
                Type: {stopVariant}
              </Typography>
            ))}
          {order.type === "dca-order" ? (
            <Typography variant="caption" color="secondary">
              Ends on {formatFullDate(order.expires_at_ts as number)}
            </Typography>
          ) : (
            order.type !== "hopper-order" && (
              <Typography variant="caption" color="secondary">
                Expires at {formatFullDate(order.expires_at_ts as number)}
              </Typography>
            )
          )}
          <Typography variant="caption" color="secondary">
            {formatRelative(order.created_at_ts as number)}
          </Typography>
        </Stack>
        <Stack alignItems="flex-end" sx={{ overflow: "hidden" }}>
          <Tooltip
            title={
              output === "n/A"
                ? null
                : parseBigIntAsNumber(BigInt(output as string), toToken?.denomination)
            }
          >
            <TokenPill
              className={css`
                font-size: 1rem;
                font-weight: 500;
              `}
            >
              <Typography
                fontSize="inherit"
                fontFamily={NumberFont}
                fontWeight={500}
                sx={{
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                }}
              >
                {output === "n/A"
                  ? "N/A"
                  : formatNumberAuto(parseBigIntAsNumber(output, toToken?.denomination))}
              </Typography>
              <TokenLogo tx={toToken?.logo} alt={`${toToken?.name} logo`} size="medium" />
              {toToken?.ticker}
            </TokenPill>
          </Tooltip>
          <Tooltip title={parseBigIntAsNumber(order.input, fromToken?.denomination)}>
            <Typography
              variant="caption"
              component={Stack}
              direction="row"
              gap={0.5}
              sx={{ maxWidth: "100%" }}
              color="secondary"
            >
              from{" "}
              {order.type !== "hopper-order" && (
                <Typography
                  fontSize="inherit"
                  fontFamily={NumberFont}
                  fontWeight={500}
                  component={"div"}
                  sx={{
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {formatNumberAuto(parseBigIntAsNumber(order.input, fromToken?.denomination))}
                </Typography>
              )}{" "}
              {fromToken?.ticker}
            </Typography>
          </Tooltip>
          {order.type !== "hopper-order" &&
            order.type !== "stop-order" &&
            (filledPrice === undefined || fromToken === undefined || toToken === undefined ? (
              <Skeleton width={110} />
            ) : (
              <Tooltip
                title={
                  filledPrice === null
                    ? null
                    : parseBigIntAsNumber(
                        filledPrice,
                        PRICE_DECIMALS - fromToken.denomination + toToken.denomination,
                      )
                }
              >
                <Typography
                  variant="caption"
                  component="div"
                  sx={{
                    maxWidth: "100%",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                  }}
                  color="secondary"
                >
                  Avg. Fill Price{" "}
                  <Typography
                    color="accent.main"
                    fontSize="inherit"
                    fontFamily={NumberFont}
                    fontWeight={500}
                    component="span"
                  >
                    {filledPrice === null
                      ? "N/A"
                      : formatNumberAuto(
                          parseBigIntAsNumber(
                            filledPrice,
                            PRICE_DECIMALS - fromToken.denomination + toToken.denomination,
                          ),
                        )}
                  </Typography>{" "}
                </Typography>
              </Tooltip>
            ))}
          {order.type === "stop-order" &&
            (stopTriggerPrice === undefined || fromToken === undefined || toToken === undefined ? (
              <Skeleton width={110} />
            ) : (
              <Tooltip
                title={parseBigIntAsNumber(
                  stopTriggerPrice,
                  PRICE_DECIMALS - fromToken.denomination + toToken.denomination,
                )}
              >
                <Typography
                  variant="caption"
                  component={Stack}
                  direction="row"
                  gap={0.5}
                  sx={{ maxWidth: "100%" }}
                  color="secondary"
                >
                  Trigger:
                  <Typography
                    fontSize="inherit"
                    fontFamily={NumberFont}
                    fontWeight={500}
                    component={"div"}
                    sx={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                    }}
                  >
                    {formatNumberAuto(
                      parseBigIntAsNumber(
                        stopTriggerPrice,
                        PRICE_DECIMALS - fromToken.denomination + toToken.denomination,
                      ),
                    )}
                  </Typography>{" "}
                  {toToken?.ticker}
                  {stopVariant === "limit" && triggerPriceHit && (
                    <Check
                      height={15}
                      width={15}
                      style={{
                        marginTop: 2,
                        color: "var(--mui-palette-success-main)",
                      }}
                    />
                  )}
                </Typography>
              </Tooltip>
            ))}{" "}
          {order.type === "stop-order" &&
            stopVariant === "limit" &&
            (stopLimitPrice === undefined || fromToken === undefined || toToken === undefined ? (
              <Skeleton width={110} />
            ) : (
              <Tooltip
                title={parseBigIntAsNumber(
                  stopLimitPrice,
                  PRICE_DECIMALS - fromToken.denomination + toToken.denomination,
                )}
              >
                <Typography
                  variant="caption"
                  component={Stack}
                  direction="row"
                  gap={0.5}
                  sx={{ maxWidth: "100%" }}
                  color="secondary"
                >
                  Limit:
                  <Typography
                    fontSize="inherit"
                    fontFamily={NumberFont}
                    fontWeight={500}
                    component={"div"}
                    sx={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                    }}
                  >
                    {formatNumberAuto(
                      parseBigIntAsNumber(
                        stopLimitPrice,
                        PRICE_DECIMALS - fromToken.denomination + toToken.denomination,
                      ),
                    )}
                  </Typography>{" "}
                  {toToken?.ticker}
                </Typography>
              </Tooltip>
            ))}{" "}
          {order.type === "stop-order" &&
            stopVariant === "trailing" &&
            (trailingDiff === undefined ? (
              <Skeleton width={110} />
            ) : (
              <Typography
                variant="caption"
                component={Stack}
                direction="row"
                gap={0.5}
                sx={{ maxWidth: "100%" }}
                color="secondary"
              >
                Trailing:
                <Typography
                  fontSize="inherit"
                  fontFamily={NumberFont}
                  fontWeight={500}
                  component={"div"}
                  sx={{
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {Number(trailingDiff) / 100}%
                </Typography>{" "}
              </Typography>
            ))}
          <Typography color="secondary">
            <Link
              href={`https://ao.link/#/entity/${order.id}`}
              target="_blank"
              rel="noopener noreferrer"
              color="secondary"
              variant="caption"
            >
              View on ao.link
            </Link>{" "}
            •{" "}
            <Link
              href={`https://dexi.arweave.net/#/entity/${order.id}`}
              target="_blank"
              rel="noopener noreferrer"
              color="secondary"
              variant="caption"
            >
              View on Dexi
            </Link>
          </Typography>
        </Stack>
      </Box>
      <Stack direction="row" gap={0.5}>
        {(agentInfo as AgentInfo)?.Status === "AwaitingDeposit" && (
          <Tooltip title="Agent creation flow was interrupted. To recover this agent you need to send it funds.">
            <Button
              color="secondary"
              fullWidth
              variant="text"
              sx={{
                padding: 0.25,
                background: "var(--mui-palette-buttonBgSecondary) !important",
                borderRadius: 0,
                fontSize: "0.75rem",
              }}
              onClick={handleTopUp}
              disabled={cancelLoading || topUpLoading}
            >
              {cancelLoading ? "Cancelling..." : "Top-up"}
            </Button>
          </Tooltip>
        )}
        <Button
          color="secondary"
          fullWidth
          variant="text"
          sx={{
            padding: 0.25,
            background: "var(--mui-palette-buttonBgSecondary) !important",
            borderRadius: 0,
            fontSize: "0.75rem",
          }}
          onClick={handleCancel}
          disabled={cancelLoading || topUpLoading}
        >
          {cancelLoading ? "Cancelling..." : "Cancel"}
        </Button>
      </Stack>
    </Paper>
  )
}
